import { useEffect, useState } from "react";
import { Package, Trash2, CopySlash } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { photoThumb, photoThumbBackend } from "@/lib/photoUrl";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";

export const DuplicateFinderDialog = ({ open, onOpenChange, onDeleted }) => {
  const { t } = useLanguage();
  const [groups, setGroups] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGroups(null);
    setChecked(new Set());
    api.get("/products/duplicates")
      .then(({ data }) => setGroups(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [open]);

  const toggle = (itemNumber) => {
    setChecked((s) => {
      const next = new Set(s);
      if (next.has(itemNumber)) next.delete(itemNumber);
      else next.add(itemNumber);
      return next;
    });
  };

  const handleDelete = async () => {
    const items = [...checked];
    setDeleting(true);
    try {
      await Promise.all(items.map((i) => api.delete(`/products/${encodeURIComponent(i)}`)));
      toast.success(`${t("dup.deleted")}: ${items.length}`);
      setChecked(new Set());
      const { data } = await api.get("/products/duplicates");
      setGroups(data);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="duplicate-finder-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            <CopySlash size={16} /> {t("dup.title")}
          </DialogTitle>
          <DialogDescription className="text-xs">{t("dup.hint")}</DialogDescription>
        </DialogHeader>

        {groups === null && <p className="text-sm text-muted-foreground py-6 text-center">...</p>}
        {groups?.length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="dup-empty-state">{t("dup.none")}</p>
        )}

        <div className="space-y-4">
          {groups?.map((g, gi) => (
            <div key={gi} className="border border-border rounded-md overflow-hidden" data-testid={`dup-group-${gi}`}>
              <div className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider ${g.type === "exact" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                {g.type === "exact" ? t("dup.exact") : t("dup.similar")}
              </div>
              {g.products.map((p) => (
                <div key={p.item_number} className="flex items-center gap-3 px-3 py-2 border-t border-border/60">
                  <Checkbox
                    checked={checked.has(p.item_number)}
                    onCheckedChange={() => toggle(p.item_number)}
                    data-testid={`dup-check-${p.item_number}`}
                  />
                  {p.photo_url ? (
                    <img src={photoThumb(p.photo_url, "sm")} alt="" className="w-12 h-10 object-contain bg-white border border-border rounded-sm" onError={(e) => { const i=e.currentTarget; if(i.dataset.f!=="1"){i.dataset.f="1";i.src=photoThumbBackend(p.photo_url,"sm");} else i.style.display="none"; }} />
                  ) : (
                    <div className="w-12 h-10 bg-secondary rounded-sm flex items-center justify-center"><Package size={13} className="text-muted-foreground" /></div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-mono font-semibold text-sm">{p.item_number}</p>
                    <p className="text-xs text-muted-foreground truncate">{p.description || "-"}</p>
                  </div>
                  <div className="text-right font-mono text-[11px] text-muted-foreground shrink-0">
                    <p>{t("dup.priceRows")}: {p.price_rows}</p>
                    <p>{(p.created_at || "").slice(0, 10)}</p>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {groups?.length > 0 && (
          <DialogFooter className="items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground mr-auto" data-testid="dup-selected-count">
              {t("import.selectedCount")}: {checked.size}
            </span>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={checked.size === 0 || deleting} data-testid="dup-delete-button" className="rounded-md">
                  <Trash2 size={14} /> {t("dup.deleteSelected")} ({checked.size})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("dup.confirmTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {[...checked].join(", ")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} data-testid="dup-delete-confirm" className="bg-destructive hover:bg-destructive/90">
                    {t("common.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
